import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { View } from "@common/types/views/views";
import { useCallback, useMemo, useState } from "react";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { createBillsUrl } from "@components/bills/lib/billsUrl";
import { createInvoicesUrl } from "@components/invoices/lib/invoicesUrl";
import { ResourceType } from "@common/types/auditLog";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import type { HistoryCreatedTransactionDetailsRow } from "./transactions/views/historyCreatedTransactionDetailsViewConfig";
import { HistoryRow } from "../views/historyViewConfig";
import {
    HistoryTransactionDetailsRow,
    createHistoryTransactionDetailsViewConfig,
} from "./transactions/views/historyTransactionDetailsViewConfig";
import styles from "./HistoryDetails.module.scss";
import { defaultHistoryTransactionDetailsView } from "./transactions/views/defaultHistoryTransactionDetailsView";
import {
    isRowDeleted,
    isRowDeletedAccrual,
    isTransactionRow,
} from "./transactions/historyTransactionRowHelpers";

export type HistoryDetailsTableMeta = ViewTableMeta<
    {
        historyRow: HistoryRow;
        onRowAction?: (row: HistoryTransactionDetailsRow) => void;
        onCreatedRowAction?: (row: HistoryCreatedTransactionDetailsRow) => void;
    },
    unknown
>;

interface HistoryUpdatedTransactionDetailsProps {
    data: HistoryTransactionDetailsRow[];
    footer: React.ReactNode;
    historyRow: HistoryRow;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
}

export const HistoryUpdatedTransactionDetails: React.FC<
    HistoryUpdatedTransactionDetailsProps
> = ({
    data,
    footer,
    historyRow,
    rowSelection: controlledRowSelection,
    onRowSelectionChange: controlledOnRowSelectionChange,
}) => {
    const [view, setView] = useState<View>(
        defaultHistoryTransactionDetailsView,
    );
    const [uncontrolledRowSelection, setUncontrolledRowSelection] =
        useState<RowSelectionState>({});

    const rowSelection = controlledRowSelection ?? uncontrolledRowSelection;
    const onRowSelectionChange =
        controlledOnRowSelectionChange ?? setUncontrolledRowSelection;

    const navigate = useStateAwareNavigate();
    const openTransactionViewDrawer = useGlobalTransactionViewDrawer();

    const viewConfig = useMemo(
        () => createHistoryTransactionDetailsViewConfig(data),
        [data],
    );

    const handleRowAction = useCallback(
        (row: HistoryTransactionDetailsRow) => {
            if (isRowDeleted(row)) {
                return;
            }
            if (row.resourceType === ResourceType.BILL) {
                navigate(createBillsUrl({ billIds: [row.resourceId] }));
                return;
            }
            if (row.resourceType === ResourceType.INVOICE) {
                navigate(createInvoicesUrl({ invoiceIds: [row.resourceId] }));
                return;
            }
            if (!isTransactionRow(row)) {
                return;
            }
            openTransactionViewDrawer({
                entityIds: historyRow.entityIds,
                transactionIds: [Number(row.resourceId)],
            });
        },
        [historyRow, navigate, openTransactionViewDrawer],
    );

    const tableMeta: HistoryDetailsTableMeta = useMemo(
        () => ({ callbacks: { historyRow, onRowAction: handleRowAction } }),
        [historyRow, handleRowAction],
    );

    const handleRowClick = useCallback(
        (row?: HistoryTransactionDetailsRow) => {
            if (!row) {
                return;
            }
            handleRowAction(row);
        },
        [handleRowAction],
    );

    const enableRowSelection = useCallback(
        (row: HistoryTransactionDetailsRow) =>
            isTransactionRow(row) &&
            !isRowDeletedAccrual(row) &&
            !isRowDeleted(row),
        [],
    );

    return (
        <ViewTable
            data={data}
            view={view}
            config={viewConfig}
            onViewChange={setView}
            onRowClick={handleRowClick}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            enableRowSelection={enableRowSelection}
            emptyView={() => <EmptyState header="No details available" />}
            footer={footer}
            tableMeta={tableMeta}
            className={styles.historyDetailsView}
            fixedTopOffsetPx
        />
    );
};
